import type { IServiceMethod } from '@/shared/api';
import { appApi } from '@/shared/api';
import { ticketControlApi } from '@/shared/api/api';
import type {
	IParticipantTournament,
	IPlayerTournament,
	IRegisterPlayerByTonRequest,
	ITournament,
	ITournamentActive,
	ITournamentHistoryResponse,
	ITournamentRegisterRequest,
	ITournamentRegisterStarsRequest,
	ITournamentRegisterStripeRequest,
	ITournamentResult,
	TIsTournamentParticipantRequest,
} from './types';

const getAllActiveTournaments: IServiceMethod<ITournamentActive[]> = () =>
	appApi.get<ITournamentActive[]>('TournamentsController/tournaments', {}, true);

getAllActiveTournaments.queryKey = 'getAllActiveTournaments';

const getActiveTournamentById: IServiceMethod<ITournamentActive, number> = (id) =>
	appApi.get<ITournamentActive>(`TournamentsController/tournaments/${id}`, {}, true);

getActiveTournamentById.queryKey = 'getActiveTournamentById';

const getAllMyTournaments: IServiceMethod<ITournament[]> = () =>
	appApi.get<ITournament[]>('TournamentsController/tournaments/history', {}, true);

getAllMyTournaments.queryKey = 'getMyTournamentsHistory';

const getAllTournamentsByPlayerId: IServiceMethod<ITournament[], number> = (id) =>
	appApi.get<ITournament[]>(`TournamentsController/tournaments/history/${id}`, {}, true);

getAllTournamentsByPlayerId.queryKey = 'getPlayerTournamentsHistory';

const getAllPlayersByTournament: IServiceMethod<IPlayerTournament[], number> = (id) =>
	appApi.get<IPlayerTournament[]>(`tournament/participant?id=${id}`, {}, true);

getAllPlayersByTournament.queryKey = 'getAllPlayersByTournamentId';

const getTournamentWinner: IServiceMethod<IPlayerTournament, number> = (id) =>
	appApi.get<IPlayerTournament>(`TournamentsController/tournaments/${id}/winner`, {}, true);

getTournamentWinner.queryKey = 'getTournamentWinner';

const registration: IServiceMethod<void, ITournamentRegisterRequest> = (body) =>
	ticketControlApi.post('api/TournamentParticipants/register', { body }, true);

registration.queryKey = 'registerForTournament';

const registrationStripe: IServiceMethod<void, ITournamentRegisterStripeRequest> = (body) =>
	ticketControlApi.post('api/TournamentParticipants/register/stripe', { body }, true);

registrationStripe.queryKey = 'registrationStripe';

const getTournamentParticipantsById: IServiceMethod<IParticipantTournament[], number> = (id) =>
	ticketControlApi.get<IParticipantTournament[]>(`api/TournamentParticipants/tournament/${id}`, {}, true);

getTournamentParticipantsById.queryKey = 'getTournamentParticipantsById';

const getTournamentsByPlayerId: IServiceMethod<IParticipantTournament[], number> = (id) =>
	ticketControlApi.get<IParticipantTournament[]>(`api/TournamentParticipants/player/${id}`, {}, true);

getTournamentsByPlayerId.queryKey = 'getTournamentsByPlayerId';

const getIsTournamentParticipant: IServiceMethod<boolean, TIsTournamentParticipantRequest> = async (body) => {
	const tournaments = await getTournamentsByPlayerId(Number(body?.playerId));
	const isParticipant = tournaments.some((t) => t.tournamentId === body?.tournamentId);

	return isParticipant;
};

getIsTournamentParticipant.queryKey = 'getIsTournamentParticipant';

const getMyPaidTournaments: IServiceMethod<ITournamentHistoryResponse[]> = () =>
	appApi.get<ITournamentHistoryResponse[]>('v2/tournaments/player', {}, true);

getMyPaidTournaments.queryKey = 'getMyPaidTournaments';

const getMyTournamentResults: IServiceMethod<ITournamentResult[]> = () =>
	appApi.get<ITournamentResult[]>('tournament/player/results', {}, true);

getMyTournamentResults.queryKey = 'getMyTournamentResults';

const getPlayerTournamentResults: IServiceMethod<ITournamentResult[], number> = (playerId) =>
	appApi.get<ITournamentResult[]>(`tournament/player/results?player_id=${playerId}`, {}, true);

getPlayerTournamentResults.queryKey = 'getPlayerTournamentResults';

const registerByTon: IServiceMethod<void, IRegisterPlayerByTonRequest> = (body) =>
	ticketControlApi.post('api/TournamentParticipants/register/ton', { body }, true);

registerByTon.queryKey = 'registerByTon';

const getMyStatisticsByTournamentId: IServiceMethod<IPlayerTournament, number> = (tournamentId) =>
	appApi.get<IPlayerTournament>(`tournament/leaderboard/player?tournament_id=${tournamentId}`, {}, true);

getMyStatisticsByTournamentId.queryKey = 'getMyStatisticsByTournamentId';

const getMyGlobalTournamentStatistics: IServiceMethod<IPlayerTournament> = () =>
	appApi.get<IPlayerTournament>('tournament/leaderboard/player', {}, true);

getMyGlobalTournamentStatistics.queryKey = 'getMyGlobalTournamentStatistics';

const registerByStars: IServiceMethod<void, ITournamentRegisterStarsRequest> = (body) =>
	ticketControlApi.post('api/TournamentParticipants/register/telegram', { body }, true);

registerByStars.queryKey = 'registerByStars';

export const TournamentRegistrationService = {
	registerByStars,
	registerByTon,
	registration,
	registrationStripe,
};

export const TournamentService = {
	getActiveTournamentById,
	getAllActiveTournaments,
	getAllMyTournaments,
	getAllPlayersByTournament,
	getIsTournamentParticipant,
	getMyGlobalTournamentStatistics,
	getMyPaidTournaments,
	getMyStatisticsByTournamentId,
	getMyTournamentResults,
	getPlayerTournamentResults,
	getTournamentParticipantsById,
	getTournamentWinner,
	getTournamentsByPlayerId,
};
